// src/components/modals/ViewCustomerGroupModal.jsx
import { X, Users, User, Phone, Calendar, Building, Award, DollarSign, Clock } from 'lucide-react'
import { CustomerGroupModel } from '../../utils/customerGroupModel'
import { CustomerModel } from '../../utils/customerModel'

const ViewCustomerGroupModal = ({ isOpen, onClose, group }) => {
  if (!isOpen || !group) return null

  // Format group data for display
  const formattedGroup = {
    ...group,
    formattedName: CustomerGroupModel.formatName(group),
    customerCount: CustomerGroupModel.getCustomerCount(group),
    formattedCustomerNames: CustomerGroupModel.formatCustomerNames(group),
    formattedCreatedAt: CustomerGroupModel.formatDate(group.createdAt),
    formattedUpdatedAt: CustomerGroupModel.formatDate(group.updatedAt)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {/* <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Group Details</h2>
              <p className="text-sm text-gray-500">View customer group information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Group Status Badge */}
        <div className="mb-6">
          <span className={CustomerGroupModel.getStatusBadgeClasses(group.isActive)}>
            {group.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Group Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Group Information</h3>
            
            {/* Group Name */}
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Group Name</p>
                <p className="text-gray-900">{group.name || 'Not available'}</p>
              </div>
            </div>

            {/* Description */}
            <div className="flex items-start space-x-3">
              <div className="p-1 mt-0.5">
                <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Description</p>
                <p className="text-gray-900">{group.description || 'No description provided'}</p>
              </div>
            </div>

            {/* Customer Count */}
            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Number of Customers</p>
                <p className="text-gray-900">{formattedGroup.customerCount} customers</p>
              </div>
            </div>
          </div>

          {/* Metadata Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Group Metadata</h3>
            
            {/* Created Date */}
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Created Date</p>
                <p className="text-gray-900">{formattedGroup.formattedCreatedAt}</p>
              </div>
            </div>

            {/* Last Updated */}
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Last Updated</p>
                <p className="text-gray-900">{formattedGroup.formattedUpdatedAt}</p>
              </div>
            </div>

            {/* Created By */}
            {group.createdBy && (
              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Created By</p>
                  <p className="text-gray-900">{group.createdBy.username || 'System'}</p>
                </div>
              </div>
            )}

            {/* Group ID */}
            <div className="flex items-start space-x-3">
              <div className="p-1 mt-0.5">
                <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Group ID</p>
                <p className="text-gray-600 font-mono text-xs">{group._id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer List */}
        {Array.isArray(group.customers) && group.customers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Customers in this Group</h3>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 gap-4 max-h-60 overflow-y-auto">
                {group.customers.map((customer, index) => (
                  <div key={customer._id || index} className="bg-white p-4 rounded-lg border">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Customer Basic Info */}
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <User className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{customer.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">
                            {CustomerModel.formatMobileNumber(customer.mobileNumber) || 'No mobile'}
                          </p>
                        </div>
                      </div>

                      {/* Customer Details */}
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            DOB: {customer.dateOfBirth ? CustomerModel.formatDate(customer.dateOfBirth) : 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Customer Stats */}
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Award className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            Loyalty: {((customer.pointsBalance || customer.loyaltyPoints || 0)).toFixed(2)} points
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State for No Customers */}
        {(!Array.isArray(group.customers) || group.customers.length === 0) && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">Customers in this Group</h3>
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No customers in this group</p>
            </div>
          </div>
        )}

        {/* Group Summary Statistics */}
        {Array.isArray(group.customers) && group.customers.length > 0 && (
          <div className="mb-6 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Group Summary</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Customers */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Customers</p>
                    <p className="text-xl font-semibold text-gray-900">{formattedGroup.customerCount}</p>
                  </div>
                </div>
              </div>
              
              {/* Active Customers */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Active Customers</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {group.customers.filter(c => c.isActive !== false).length}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Total Loyalty Points */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Loyalty Points</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {group.customers.reduce((sum, c) => sum + (c.pointsBalance || c.loyaltyPoints || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewCustomerGroupModal